import Sentry from '../sentry';

const CLEARFEED_CLIENT_ID = process.env.CLEARFEED_CLIENT_ID;

declare global {
  interface Window {
    ClearFeed?: (action: string, config: Record<string, unknown>) => void;
  }
}

let isLoaded = false;
let isInitialized = false;

const load = (onReady?: () => void) => {
  if (isLoaded || document.getElementById('cf-widget-script')) {
    isLoaded = true;
    onReady?.();
    return;
  }

  const script = document.createElement('script');
  script.id = 'cf-widget-script';
  script.src = 'https://cdn.clearfeed.app/chat-widget.js';
  script.async = true;
  script.onload = () => {
    isLoaded = true;
    onReady?.();
  };
  script.onerror = (event) => {
    const err = new Error('[ClearFeed] Failed to load chat widget script');
    console.error(err.message, event);
    Sentry.captureException(err, {
      tags: { service: 'clearfeed', phase: 'script-load' },
      extra: { src: script.src },
    });
  };
  document.body.appendChild(script);
};

const init = (user?: { name: string; email: string; hash?: string }) => {
  if (!isLoaded || !window.ClearFeed) {
    console.warn('[ClearFeed] Script not loaded yet.');
    return;
  }
  if (isInitialized) return;

  try {
    // Only pass the user block when we have an HMAC hash — ClearFeed treats unhashed
    // user data as spoofable and we'd rather fall back to anonymous than send unverified
    // identity claims.
    const verifiedUser =
      user?.hash && user.name && user.email
        ? { name: user.name, email: user.email, hash: user.hash }
        : undefined;

    window.ClearFeed('init', {
      client_id: CLEARFEED_CLIENT_ID,
      user: verifiedUser,
      config: {
        brand_color: '#1f7a1f',
        icon_logo_color: '#ffffff',
        icon_type: 'round',
        icon_size: 'medium',
        position: 'right',
        allow_dragging: false,
        offset: { x: 40, y: 40 },
      },
    });
    isInitialized = true;
  } catch (e) {
    console.error('[ClearFeed] Failed to initialize:', e);
    Sentry.captureException(e, {
      tags: { service: 'clearfeed', phase: 'init' },
    });
  }
};

const boot = (user?: { name: string; email: string; hash?: string }) => {
  load(() => init(user));
};

// ---------------------------------------------------------------------------
// Prefill the chat composer
//
// ClearFeed exposes no API to set the composer text (only init/open/close/
// identify), and it renders its chat UI inside an OPEN Shadow DOM on
// <div id="clearfeed-host">. So the only way to prefill is to write into the
// composer (`<textarea id="message-textarea">`) inside that shadow root.
//
// The one hard part is timing: the widget loads/navigates asynchronously, and
// writing before it has settled gets discarded. Instead of polling, we use a
// MutationObserver and act only once the shadow DOM has been QUIET for
// SETTLE_MS — i.e. it has finished loading/navigating. Then:
//   - if the composer is on screen, set its value once (native setter + input
//     event so React registers it);
//   - if we're on the conversation list, click "Start a new conversation" once
//     and fill on the next settle.
// We stop as soon as our text is in place (and send, if asked), or after a hard
// timeout. No polling, no retry loops.
// ---------------------------------------------------------------------------

const CF = {
  hostId: 'clearfeed-host', // light-DOM host; its .shadowRoot holds the widget
  textareaId: 'message-textarea', // composer, inside the shadow root
  newConversationTexts: ['new conversation', 'start a new conversation'],
  sendButtonSelector:
    'button[aria-label*="Send" i]:not([disabled]), button[type="submit"]:not([disabled])',
};

const SETTLE_MS = 400; // treat the widget as "loaded" after this long with no DOM mutations
const MAX_WAIT_MS = 8000; // give up waiting for the composer after this

const setComposerValue = (textarea: HTMLTextAreaElement, text: string) => {
  // Native value setter + `input` event: the standard way to make a React-
  // controlled input notice a programmatic change.
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    'value'
  )?.set;
  nativeInputValueSetter?.call(textarea, text);
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
  textarea.focus();
};

const findNewConversationButton = (root: ParentNode) => {
  const buttons = root.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
  for (const btn of Array.from(buttons)) {
    const label = btn.textContent?.trim().toLowerCase() ?? '';
    if (CF.newConversationTexts.some((t) => label.includes(t))) return btn;
  }
  return null;
};

const findSendButton = (root: ParentNode) =>
  root.querySelector<HTMLButtonElement>(CF.sendButtonSelector);

// ClearFeed's buttons ignore a bare .click() in some states, so dispatch a full
// pointer sequence so its React handlers fire.
const robustClick = (el: HTMLElement) => {
  const opts = { bubbles: true, cancelable: true, view: window };
  el.dispatchEvent(new PointerEvent('pointerdown', opts));
  el.dispatchEvent(new MouseEvent('mousedown', opts));
  el.dispatchEvent(new PointerEvent('pointerup', opts));
  el.dispatchEvent(new MouseEvent('mouseup', opts));
  el.click();
};

// Only one prefill run at a time.
let activeObserver: MutationObserver | null = null;

/**
 * Open the widget and, once its shadow-DOM UI has settled, prefill the composer
 * once (optionally auto-sending). A bare open (no text) just shows the widget.
 */
const openWithPrefill = (text: string | undefined, autoSend: boolean) => {
  if (!isInitialized) {
    console.warn('[ClearFeed] Widget not initialized yet.');
    return;
  }

  try {
    window.ClearFeed?.('open', {});
  } catch (e) {
    console.error('[ClearFeed] Failed to open widget:', e);
    Sentry.captureException(e, { tags: { service: 'clearfeed', phase: 'open' } });
    return;
  }

  if (!text?.trim()) return; // bare open

  const shadow = document.getElementById(CF.hostId)?.shadowRoot;
  if (!shadow) {
    Sentry.captureMessage('[ClearFeed] shadow root not found for prefill', {
      level: 'warning',
      tags: { service: 'clearfeed', phase: 'prefill' },
    });
    return;
  }

  activeObserver?.disconnect();

  const target = text.trim();
  let clickedNewConversation = false;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const stop = () => {
    stopped = true;
    if (settleTimer) clearTimeout(settleTimer);
    observer.disconnect();
    if (activeObserver === observer) activeObserver = null;
  };

  // Runs after the widget's shadow DOM has been quiet for SETTLE_MS (loaded /
  // done navigating). Acting only when settled is what makes the write stick.
  const onSettled = () => {
    if (stopped) return;

    const composer = shadow.querySelector<HTMLTextAreaElement>(`#${CF.textareaId}`);
    if (!composer) {
      // On the conversation list — open a new conversation once; we'll fill on
      // the next settle.
      if (!clickedNewConversation) {
        const btn = findNewConversationButton(shadow);
        if (btn) {
          robustClick(btn);
          clickedNewConversation = true;
        }
      }
      return;
    }

    const current = composer.value.trim();
    if (current === target) {
      // Our text is in place. Send if asked; either way we're done.
      if (autoSend) {
        const sendBtn = findSendButton(shadow);
        if (sendBtn) robustClick(sendBtn);
      }
      stop();
      return;
    }
    if (!current) {
      // Empty (fresh, or ClearFeed cleared it) — fill once. The next settle
      // confirms it stuck (and sends).
      setComposerValue(composer, text);
      return;
    }
    // Composer already holds different text (the user started typing) — leave it.
    stop();
  };

  const observer = new MutationObserver(() => {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(onSettled, SETTLE_MS);
  });
  activeObserver = observer;
  observer.observe(shadow, { childList: true, subtree: true });

  // Kick off (covers an already-settled widget) and guarantee we stop.
  settleTimer = setTimeout(onSettled, SETTLE_MS);
  setTimeout(stop, MAX_WAIT_MS);
};

// Open the widget, optionally prefilling the composer with `prefill`.
const open = (prefill?: string) => openWithPrefill(prefill, false);

// Open the widget, prefill the composer AND submit it.
const openAndSend = (text: string) => openWithPrefill(text, true);

const clearfeed = { load, init, boot, open, openAndSend };

export default clearfeed;
